import {computed, Inject, inject, Injectable, OnDestroy, signal} from '@angular/core';
import Pusher, {Channel} from 'pusher-js';
import {filter, map, Observable, Subject} from 'rxjs';
import {SessionStorageService} from '../storage';
import {UserRepositoryService} from '../repositories/user-repository/user-repository.service';
import {WebSocketEvent, WebSocketSubscribe, WebSocketUnsubscribe} from '../interfaces';
import {PUSHER_CONFIG, PusherConfig} from '../config';
import {parsePusherPayload} from './websocket-payload';

type ConnectionState = 'connected' | 'connecting' | 'disconnected' | 'failed' | 'unavailable' | 'error';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy, WebSocketSubscribe, WebSocketUnsubscribe {
  private sessionStorage = inject(SessionStorageService);
  private pusher: Pusher;

  // Connection state as signals
  readonly connectionState = signal<ConnectionState>('connecting');
  readonly socketId$ = signal<string>('');
  readonly isConnected = computed(() => this.connectionState() === 'connected');

  // Legacy socketId property for backward compatibility
  get socketId(): string {
    return this.socketId$();
  }

  // Internal event stream - fed by all Pusher bindings
  private eventStream$ = new Subject<WebSocketEvent>();

  // Track which channel/event combos have Pusher bindings
  private boundEvents = new Set<string>();

  // Maps for tracking subscriptions
  private subscribedChannels = new Map<string, Channel>();
  private callbackRegistry = new Map<string, Set<(data: any) => void>>();

  private userRepo = inject(UserRepositoryService);

  constructor(@Inject(PUSHER_CONFIG) private pusherConfig: PusherConfig) {
    const pusherOptions: any = {
      cluster: pusherConfig.cluster,
      forceTLS: true,
      channelAuthorization: {
        transport: 'custom',
        customHandler: (params: any, callback: (error: any, authData: any) => void) => {
          this.userRepo.pusherAuth(params.socketId, params.channelName).subscribe({
            next: (response: any) => callback(null, response),
            error: (err) => callback(err, null)
          });
        }
      }
    };
    this.pusher = new Pusher(pusherConfig.key, pusherOptions);
    this.bindConnectionEvents();
  }

  private bindConnectionEvents(): void {
    this.pusher.connection.bind('connected', (event: any) => {
      this.socketId$.set(event.socket_id);
      this.connectionState.set('connected');
      this.sessionStorage.setItem('socket_id', event.socket_id);
    });
    this.pusher.connection.bind('failed', (error: any) => {
      this.connectionState.set('failed');
      console.error('connection failed', error);
    });
    this.pusher.connection.bind('unavailable', _ => {
      this.connectionState.set('unavailable');
      console.warn('connection unavailable');
    });
    this.pusher.connection.bind('connecting', (event: any) => {
      this.connectionState.set('connecting');
      console.log('connecting', event);
    });
    this.pusher.connection.bind('disconnected', (event: any) => {
      this.connectionState.set('disconnected');
      console.warn('disconnected	', event);
    });
    this.pusher.connection.bind('error', (error: any) => {
      this.connectionState.set('error');
      console.error('error', error);
    });
  }

  ngOnDestroy(): void {
    this.subscribedChannels.forEach(channel => channel.unbind_all());
    this.eventStream$.complete();
    this.sessionStorage.deleteItem('socket_id');
  }

  /**
   * Subscribe to a channel/event and get an Observable stream.
   * Multiple subscribers share the same underlying Pusher binding.
   * Use with takeUntilDestroyed() for automatic cleanup.
   *
   * @example
   * ```typescript
   * this.websocket.on<MessageData>('practice-123', 'message_received')
   *   .pipe(takeUntilDestroyed())
   *   .subscribe(data => this.handleMessage(data));
   * ```
   */
  on<T = unknown>(channelName: string, eventName: string): Observable<T> {
    const channel = channelName.toLowerCase();
    this.ensureEventBound(channel, eventName);

    return this.eventStream$.pipe(
      filter(e => e.channel === channel && e.event === eventName),
      map(e => e.data as T)
    );
  }

  /**
   * Subscribe to a channel/event with a callback.
   * For new code, prefer using on() which returns an Observable.
   *
   * @param channelName - The channel to subscribe to
   * @param eventName - The event to listen for
   * @param callback - Function to call when event is received
   */
  subscribe(channelName: string, eventName: string, callback: (data: any) => void): void {
    const channel = channelName.toLowerCase();
    const key = `${channel}:${eventName}`;

    // Ensure the Pusher binding exists
    this.ensureEventBound(channel, eventName);

    // Register this callback
    if (!this.callbackRegistry.has(key)) {
      this.callbackRegistry.set(key, new Set());
    }
    this.callbackRegistry.get(key)!.add(callback);
  }

  /**
   * Unsubscribe from a channel/event.
   *
   * @param channelName - The channel to unsubscribe from
   * @param eventName - The event to stop listening for
   * @param callback - Optional specific callback to remove. If not provided, removes all callbacks for this event.
   */
  unsubscribe(channelName: string, eventName: string, callback?: (data: any) => void): void {
    const channel = channelName.toLowerCase();
    const key = `${channel}:${eventName}`;
    const callbacks = this.callbackRegistry.get(key);

    if (!callbacks) return;

    if (callback) {
      // Remove specific callback only
      callbacks.delete(callback);
    } else {
      // Legacy behavior: remove all callbacks for this event (backward compatible)
      callbacks.clear();
    }
  }

  /**
   * Subscribe to a Pusher channel without binding any events.
   * Used for presence/online-status channels where the backend only checks occupancy.
   *
   * @param channelName - The channel to subscribe to (e.g., 'private-user-123')
   */
  subscribeChannel(channelName: string): void {
    const channel = channelName.toLowerCase();
    if (!this.subscribedChannels.has(channel)) {
      const newChannel = this.pusher.subscribe(channel);
      this.subscribedChannels.set(channel, newChannel);
    }
  }

  /**
   * Ensures a Pusher binding exists for the given channel/event combination.
   * Creates the binding if it doesn't exist, which will feed both the Observable stream
   * and registered callbacks.
   */
  private ensureEventBound(channelName: string, eventName: string): void {
    const key = `${channelName}:${eventName}`;
    if (this.boundEvents.has(key)) return;

    // Ensure channel is subscribed
    if (!this.subscribedChannels.has(channelName)) {
      const newChannel = this.pusher.subscribe(channelName);
      this.subscribedChannels.set(channelName, newChannel);
    }

    const channel = this.subscribedChannels.get(channelName)!;

    // Single Pusher binding that feeds the Subject AND calls registered callbacks
    channel.bind(eventName, (data: unknown) => {
      const parsedData = parsePusherPayload(data);

      // Emit to Observable stream
      this.eventStream$.next({channel: channelName, event: eventName, data: parsedData});

      // Call registered callbacks (for subscribe() pattern). Isolate each so
      // a throwing consumer can't prevent downstream consumers from receiving
      // the event.
      const callbacks = this.callbackRegistry.get(key);
      if (callbacks) {
        callbacks.forEach(cb => {
          try {
            cb(parsedData);
          } catch (err) {
            console.error(`Pusher callback for ${channelName}:${eventName} threw`, err);
          }
        });
      }
    });

    this.boundEvents.add(key);
  }
}
